import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps } from '~commonInfra/utils/ConnectUtils';
import { UrlParamKeys } from '~src/commonInfra/url/UrlParamKeys';
import { enterSpace } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '~src/spacesPlatform/actions/filters/FilterActions';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { setOrderAction } from '~src/spacesPlatform/actions/SetOrderAction';
import { getOrderClausesFromQueryParam } from '~src/spacesPlatform/components/Explorer/ExplorerUrlBindings';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ContentFilterKeyName,ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '~src/spacesPlatform/models/sort/SortOptionDefinitions';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { AllServerUsersSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { ConnectedQueryParamUpdateProvider } from '../../../../bootstrap/routerHelpers/QueryParamUpdateProvider';
import { AllServerUsersAndIdentitiesSpaceOwnProps } from './AllServerUsersAndIdentitiesSpace';
import { ConnectedAllServerUsersAndIdentitiesSpace } from './ConnectedAllServerUsersAndIdentitiesSpace';

export interface AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly subspaceName: AllServerUsersSubspaceName;

  readonly renderSpace: (props: AllServerUsersAndIdentitiesSpaceOwnProps) => JSX.Element;
  readonly routeLocationSearch: string;
}

export interface AllServerUsersAndIdentitiesSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
}

export type AllServerUsersAndIdentitiesSpaceProviderContainerProps = AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps & AllServerUsersAndIdentitiesSpaceProviderDispatchProps;

export class AllServerUsersAndIdentitiesSpaceProviderContainer extends React.PureComponent<AllServerUsersAndIdentitiesSpaceProviderContainerProps> {
  public render() {
    return this.props.renderSpace(this.props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: AllServerUsersAndIdentitiesSpaceProviderContainerProps) {
    // This is a workaround because ConnectedSpaceInfoProvider does not have logic for enter space on component update.
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  private changeSubspace() {
    let spaceInfo: SpaceInfo = {
      item: SpaceItemForNonEntitySpace,
      spaceType: 'serverusers',
      subspaceName: this.props.subspaceName,
      // Identities subspace does not support infinite scroll feature.
      isKeysetPaginationSpace: FeatureFlags.serverUserSolrMigrationEnabled && this.props.subspaceName === 'list'
    };
    this.props.enterSpace(spaceInfo);
    this.clearAndSetSortOrderFromURL(spaceInfo);
    this.setFilterTypesFromUrl();
    this.props.requestSpaceContents();
  }

  private clearAndSetSortOrderFromURL(spaceInfo: SpaceInfo) {
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(['serveruser'], spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private setFilterTypesFromUrl() {
    const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
    for (const filterType of _.keys(ContentFiltersKeys)) {
      const value = currentQueryValues[filterType];
      this.props.setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value ? value + '' : undefined);
    }
  }
}

const mapDispatchToProps: MapDispatchToProps<AllServerUsersAndIdentitiesSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
  setOrderAction,
  setContentFilterNoRequestContent
}, dispatch);

export const ConnectedAllServerUsersAndIdentitiesSpaceProviderContainer: React.ComponentClass<AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps>
  = connect(undefined, mapDispatchToProps)(AllServerUsersAndIdentitiesSpaceProviderContainer);

const renderSpace = (props: AllServerUsersAndIdentitiesSpaceOwnProps): JSX.Element => <ConnectedAllServerUsersAndIdentitiesSpace {...props} />;

export interface AllServerUsersAndIdentitiesSpaceProviderOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}
export type AllServerUsersAndIdentitiesSpaceProviderProps = AllServerUsersAndIdentitiesSpaceProviderOwnProps & Omit<AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps, 'renderSpace'>;

export const AllServerUsersAndIdentitiesSpaceProvider: React.FunctionComponent<AllServerUsersAndIdentitiesSpaceProviderProps> = (props) => {

  let spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'serverusers',
    subspaceName: props.subspaceName,
    // Identities subspace does not support infinite scroll feature.
    isKeysetPaginationSpace: FeatureFlags.serverUserSolrMigrationEnabled && props.subspaceName === 'list'
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedAllServerUsersAndIdentitiesSpaceProviderContainer {...props} renderSpace={renderSpace}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};
